import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  pt: {
    eyebrow: 'Disciplinas · Your Broker Portugal',
    heroTitle: 'Sete disciplinas.',
    heroTitleEm: 'Um único cliente.',
    heroLead:
      'Não vendemos imóveis. Representamos compradores. Cada serviço abaixo existe para que uma única decisão — a sua aquisição em Portugal — seja tomada com a profundidade que merece.',
    heroCta: 'Iniciar conversa',

    services: [
      {
        num: '01',
        title: 'Representação do comprador',
        subtitle: 'A base de tudo o que fazemos.',
        body: 'Representamos exclusivamente o comprador, do briefing inicial à entrega de chaves. Não temos imóveis para vender, não temos quota para cumprir. A nossa única referência é o seu projeto.',
        tags: ['Buyer Agency', 'Sem conflito', 'Mandato exclusivo'],
      },
      {
        num: '02',
        title: 'Acesso off-market',
        subtitle: 'O que não está em portal.',
        body: 'Cerca de 30% dos imóveis premium em Portugal nunca chegam a um portal público — vendem-se por rede privada entre escritórios, advogados e family offices. Trabalhamos esta rede há mais de uma década. Quando faz sentido para o seu projeto, abrimos-lhe a porta.',
        tags: ['Pre-market', 'Confidencial', 'Curadoria privada'],
      },
      {
        num: '03',
        title: 'Consultoria de investimento',
        subtitle: 'Quando o objetivo é retorno.',
        body: 'Análise comparada entre regiões, segmentos e formatos (residencial, branded residences, hotelaria). Projeções de yield, valorização patrimonial e cenários de saída. Apresentamos o que faz sentido financeiro — e o que não faz, mesmo que esteja em catálogo.',
        tags: ['Yield analysis', 'Exit strategy', 'Mercado comparativo'],
      },
      {
        num: '04',
        title: 'Due diligence',
        subtitle: 'Antes do CPCV.',
        body: 'Verificação jurídica completa (registo predial, urbanístico, fiscal, condomínio), técnica (estrutura, infiltrações, instalações) e contratual. Coordenamos advogado independente e técnico qualificado. O que vemos em três semanas evita um erro de quinze anos.',
        tags: ['Legal', 'Técnica', 'Contratual'],
      },
      {
        num: '05',
        title: 'Negociação independente',
        subtitle: 'Em nome do comprador. Apenas.',
        body: 'Negociamos preço, condições, contingências e calendário com o vendedor ou a mediadora vendedora. Sem favores recíprocos. Sem agenda dupla. Apresentamos a sua oferta com a estrutura e a postura que protege o seu interesse — ainda que isso atrase o fecho.',
        tags: ['Estratégia', 'Mandato exclusivo', 'Buyer-side'],
      },
      {
        num: '06',
        title: 'Relocation & lifestyle',
        subtitle: 'A vida que começa após a compra.',
        body: 'NIF, abertura de conta bancária, vistos D7/D8, NHR (regime sucessor), escolas internacionais, healthcare premium, equipa doméstica, design de interiores. Coordenamos uma rede curada de parceiros qualificados. Não cobramos margem sobre eles.',
        tags: ['NIF & Visa', 'Schools & Healthcare', 'Interior design'],
      },
      {
        num: '07',
        title: 'Concierge contínuo',
        subtitle: 'A relação não acaba na escritura.',
        body: 'Apoio permanente para os clientes que assim o pedem: gestão de obras de renovação, supervisão de manutenção, gestão de arrendamento de longa duração ou alojamento local, decisões fiscais anuais, eventual revenda. A confiança construída num mandato dura décadas.',
        tags: ['Renovação', 'Property management', 'Asset stewardship'],
      },
    ],

    feeEyebrow: 'Modelo de remuneração',
    feeTitle: 'Sem custo para o comprador.',
    feeBody:
      'A nossa remuneração sai da comissão de mercado paga pela parte vendedora — estrutura padrão do imobiliário em Portugal. O comprador não paga retenção nem honorários à Your Broker Portugal. Apenas suporta os custos legais inerentes a qualquer aquisição: IMT, Imposto do Selo, honorários notariais, registo e advogado independente. A nossa independência vem da clareza do mandato, não de quem assina o cheque.',

    closingTitle: 'Cada mandato começa por uma conversa.',
    closingBody:
      'Trinta minutos, por vídeo, sem agenda comercial. Compreendemos o seu projeto, partilhamos o nosso modelo, e indicamos honestamente se somos o parceiro certo.',
    closingCta: 'Iniciar conversa privada',
  },
  en: {
    eyebrow: 'Disciplines · Your Broker Portugal',
    heroTitle: 'Seven disciplines.',
    heroTitleEm: 'One single client.',
    heroLead:
      'We do not sell properties. We represent buyers. Each service below exists so that one decision — your acquisition in Portugal — is taken with the depth it deserves.',
    heroCta: 'Begin a conversation',

    services: [
      {
        num: '01',
        title: 'Buyer representation',
        subtitle: 'The foundation of everything we do.',
        body: 'We represent the buyer exclusively, from initial briefing through to handover. We have no properties to sell, we have no quota to meet. Our only reference is your project.',
        tags: ['Buyer Agency', 'No conflict', 'Exclusive mandate'],
      },
      {
        num: '02',
        title: 'Off-market access',
        subtitle: 'What never reaches a portal.',
        body: 'Roughly 30% of premium properties in Portugal never reach a public portal — they trade via a private network between offices, lawyers and family offices. We have worked this network for over a decade. When it makes sense for your project, we open the door.',
        tags: ['Pre-market', 'Confidential', 'Private curation'],
      },
      {
        num: '03',
        title: 'Investment advisory',
        subtitle: 'When the objective is return.',
        body: 'Comparative analysis across regions, segments and formats (residential, branded residences, hospitality). Yield projections, capital appreciation and exit scenarios. We present what makes financial sense — and what does not, even if it sits on a catalogue.',
        tags: ['Yield analysis', 'Exit strategy', 'Comparative market'],
      },
      {
        num: '04',
        title: 'Due diligence',
        subtitle: 'Before the promissory contract.',
        body: 'Complete legal verification (land registry, urbanism, tax, condominium), technical (structure, water damage, installations) and contractual. We coordinate an independent lawyer and qualified surveyor. What we see in three weeks avoids a fifteen-year mistake.',
        tags: ['Legal', 'Technical', 'Contractual'],
      },
      {
        num: '05',
        title: 'Independent negotiation',
        subtitle: 'On behalf of the buyer. Only.',
        body: 'We negotiate price, conditions, contingencies and timeline with the seller or selling agency. No reciprocal favours. No double agenda. We present your offer with the structure and posture that protects your interest — even if that delays closing.',
        tags: ['Strategy', 'Exclusive mandate', 'Buyer-side'],
      },
      {
        num: '06',
        title: 'Relocation & lifestyle',
        subtitle: 'The life that begins after the purchase.',
        body: 'NIF, bank account opening, D7/D8 visas, NHR (successor regime), international schools, premium healthcare, household staff, interior design. We coordinate a curated network of qualified partners. We do not take margin on them.',
        tags: ['NIF & Visa', 'Schools & Healthcare', 'Interior design'],
      },
      {
        num: '07',
        title: 'Continuing concierge',
        subtitle: 'The relationship does not end at the deed.',
        body: 'Permanent support for clients who request it: renovation project management, maintenance oversight, long-term or short-term rental management, annual tax decisions, eventual resale. The trust built in one mandate lasts decades.',
        tags: ['Renovation', 'Property management', 'Asset stewardship'],
      },
    ],

    feeEyebrow: 'Compensation model',
    feeTitle: 'No cost to the buyer.',
    feeBody:
      'Our compensation comes from the market commission paid by the seller — the standard structure of Portuguese real estate. The buyer pays no retainer, no fees to Your Broker Portugal. The buyer only bears the legal costs inherent to any acquisition: IMT (transfer tax), Stamp Duty, notary fees, registry and independent lawyer. Our independence comes from the clarity of the mandate, not from who signs the cheque.',

    closingTitle: 'Each mandate begins with a conversation.',
    closingBody:
      'Thirty minutes, by video, with no commercial agenda. We understand your project, share our model, and tell you honestly whether we are the right partner.',
    closingCta: 'Begin a private conversation',
  },
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const ServicesPage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const { title: seoTitle, description: seoDescription } = usePageSEO('servicos', language);
  const ref = useReveal();
  const [homeContent, setHomeContent] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} url="https://yourbrokerportugal.com/services" />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
      `}</style>

      <Header companyName={homeContent?.headerFooter?.companyName} logo={homeContent?.headerFooter?.logo} />

      <main ref={ref}>
        {/* HERO */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-9 max-w-4xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 4.4vw, 4rem)', lineHeight: '1.08' }}
              data-testid="services-hero-title"
            >
              {copy.heroTitle}{' '}
              <em className="italic text-editorial-bronze">{copy.heroTitleEm}</em>
            </h1>
            <p
              className="rv text-editorial-muted font-light max-w-2xl mb-10"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              {copy.heroLead}
            </p>
            <div className="rv flex flex-wrap gap-4 items-center">
              <a
                href={buildWhatsAppUrl('services', language)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-editorial-bone bg-editorial-charcoal hover:bg-editorial-charcoal-soft transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  borderRadius: 0,
                }}
                data-testid="services-hero-cta-whatsapp"
              >
                {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
              </a>
              <Link
                to={localizedPath('/contact')}
                className="text-editorial-charcoal-soft hover:text-editorial-bronze border-b border-editorial-charcoal-soft hover:border-editorial-bronze pb-0.5 transition-colors"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
                data-testid="services-hero-cta"
              >
                {copy.heroCta}
              </Link>
            </div>
          </div>
        </section>

        {/* 7 SERVICES — editorial alternating split */}
        <section className="bg-editorial-sand/40">
          {copy.services.map((s, i) => {
            const isEven = i % 2 === 0;
            const isDark = i === 3 || i === 6; // services 04 and 07 in charcoal
            const bg = isDark ? 'bg-editorial-charcoal text-editorial-bone' : 'bg-editorial-mineral';
            return (
              <article
                key={s.num}
                className={`px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-32 ${bg} ${i < copy.services.length - 1 ? '' : ''}`}
                data-testid={`service-${s.num}`}
              >
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                  {/* Number */}
                  <div className={`rv lg:col-span-3 ${isEven ? '' : 'lg:order-2 lg:text-right'}`}>
                    <span
                      className={`block font-light ${isDark ? 'text-editorial-bronze-lt' : 'text-editorial-sand-deep'}`}
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(4rem, 8vw, 7rem)', lineHeight: 1 }}
                    >
                      {s.num}
                    </span>
                  </div>

                  {/* Content */}
                  <div className={`lg:col-span-9 ${isEven ? '' : 'lg:order-1'}`}>
                    <div className="rv">
                      <h2
                        className={`font-light mb-3 ${isDark ? 'text-editorial-bone' : 'text-editorial-charcoal'}`}
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.7rem, 2.6vw, 2.4rem)', lineHeight: '1.18' }}
                      >
                        {s.title}
                      </h2>
                      <p
                        className={`italic mb-7 ${isDark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'}`}
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem', fontWeight: 300, lineHeight: 1.5 }}
                      >
                        {s.subtitle}
                      </p>
                      <p
                        className={`font-light max-w-2xl mb-8 ${isDark ? 'text-editorial-bone/65' : 'text-editorial-muted'}`}
                        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
                      >
                        {s.body}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {s.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`inline-block py-1.5 px-4 border ${
                              isDark
                                ? 'border-editorial-bone/20 text-editorial-bone/70'
                                : 'border-editorial-sand-deep text-editorial-charcoal-soft'
                            }`}
                            style={{
                              fontFamily: 'DM Sans, sans-serif',
                              fontSize: '0.62rem',
                              letterSpacing: '0.16em',
                              textTransform: 'uppercase',
                              fontWeight: 500,
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        {/* FEE MODEL — sand split */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-32 bg-editorial-sand/60">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rv">
              <Eyebrow>{copy.feeEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7 mb-9"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.4vw, 3.2rem)', lineHeight: '1.18' }}
              >
                {copy.feeTitle}
              </h2>
              <p
                className="text-editorial-muted font-light max-w-2xl mx-auto"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.feeBody}
              </p>
            </div>
          </div>
        </section>

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rv">
              <h2
                className="text-editorial-bone font-light mb-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.6vw, 3.2rem)', lineHeight: '1.12' }}
              >
                {copy.closingTitle}
              </h2>
              <p
                className="text-editorial-bone/55 max-w-2xl mx-auto font-light mb-12"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.closingBody}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-center">
                <a
                  href={buildWhatsAppUrl('services', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    borderRadius: 0,
                  }}
                  data-testid="services-closing-cta-whatsapp"
                >
                  {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
                </a>
                <Link
                  to={localizedPath('/contact')}
                  className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  data-testid="services-closing-cta"
                >
                  {language === 'pt' ? 'Ou enviar pedido privado' : 'Or send a private enquiry'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default ServicesPage;
